// Rank snapshot store: saves per-country popularity signals after each comparison
// view and computes ▲/▼ deltas on the next visit to the same app.
//
// Storage key: gar_snapshots
// Bounds: up to MAX_SNAPSHOTS_PER_APP snapshots per app, MAX_APPS apps total.
// Eviction: per-app LRU on lastAccessed when the app cap is exceeded.
//
// All functions accept an optional `store` parameter (an object with
// getItem/setItem/removeItem) for unit testing without jsdom/localStorage.

const STORAGE_KEY = 'gar_snapshots';
const MAX_SNAPSHOTS_PER_APP = 5;
const MAX_APPS = 30;

/**
 * @typedef {object} CountrySnapshot
 * @property {number|null} popularityScore
 * @property {number|null} userRatingCount
 * @property {number|null} averageUserRating
 */

/**
 * @typedef {object} AppSnapshot
 * @property {string} snappedAt   - ISO 8601 datetime
 * @property {Record<string, CountrySnapshot>} countries
 */

/**
 * @typedef {object} AppEntry
 * @property {string} lastAccessed  - ISO 8601 datetime (latest snapshot time)
 * @property {AppSnapshot[]} snapshots
 */

/**
 * @typedef {Record<string, AppEntry>} SnapshotStore
 */

/**
 * @typedef {object} PopularityDelta
 * @property {number} value         - Signed delta (positive = up, negative = down)
 * @property {string} snappedAt     - ISO timestamp of the prior snapshot used
 */

/**
 * Read the full snapshot map from storage.
 * @param {Storage} [store]
 * @returns {SnapshotStore}
 */
export function readStore(store) {
  const s = store ?? window.localStorage;
  try {
    const raw = s.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) ? parsed : {};
  } catch {
    return {};
  }
}

/**
 * Write the full snapshot map to storage.
 * @param {SnapshotStore} data
 * @param {Storage} [store]
 */
function writeStore(data, store) {
  const s = store ?? window.localStorage;
  try {
    s.setItem(STORAGE_KEY, JSON.stringify(data));
  } catch {
    // Storage quota exceeded — skip silently.
  }
}

/**
 * Save a snapshot for `appId` using the current row array.
 * Rows must already have `popularityScore` attached (i.e., after rankByPopularity).
 *
 * @param {string|number} appId
 * @param {Array<{countryCode: string, popularityScore?: number|null, userRatingCount?: number|null, averageUserRating?: number|null}>} rows
 * @param {string} nowIso  - ISO 8601 timestamp (injected for testability)
 * @param {Storage} [store]
 */
export function saveSnapshot(appId, rows, nowIso, store) {
  if (!appId || !Array.isArray(rows) || rows.length === 0) return;
  const key = String(appId);
  const data = readStore(store);

  const countries = /** @type {Record<string, CountrySnapshot>} */ ({});
  for (const row of rows) {
    if (!row.countryCode) continue;
    countries[row.countryCode] = {
      popularityScore: row.popularityScore ?? null,
      userRatingCount: row.userRatingCount ?? null,
      averageUserRating: row.averageUserRating ?? null,
    };
  }

  const snapshot = { snappedAt: nowIso, countries };

  if (data[key]) {
    data[key].lastAccessed = nowIso;
    data[key].snapshots.push(snapshot);
    // Keep only the most recent N snapshots.
    if (data[key].snapshots.length > MAX_SNAPSHOTS_PER_APP) {
      data[key].snapshots = data[key].snapshots.slice(-MAX_SNAPSHOTS_PER_APP);
    }
  } else {
    // Evict LRU entry if at cap.
    const appKeys = Object.keys(data);
    if (appKeys.length >= MAX_APPS) {
      const lruKey = appKeys.sort(
        (a, b) => (data[a].lastAccessed ?? '').localeCompare(data[b].lastAccessed ?? ''),
      )[0];
      delete data[lruKey];
    }
    data[key] = { lastAccessed: nowIso, snapshots: [snapshot] };
  }

  writeStore(data, store);
}

/**
 * Compute popularity deltas between `currentRows` and the most recent stored
 * snapshot for `appId`. Returns a Map<countryCode, PopularityDelta | null>.
 *
 * A null value for a country means no prior data was available for it.
 * Returns an empty Map (and `snappedAt: null`) when no prior snapshot exists.
 *
 * @param {string|number} appId
 * @param {Array<{countryCode: string, popularityScore?: number|null}>} currentRows
 * @param {Storage} [store]
 * @returns {{ deltas: Map<string, PopularityDelta|null>, snappedAt: string|null }}
 */
export function getDeltas(appId, currentRows, store) {
  const key = String(appId);
  const data = readStore(store);
  const entry = data[key];

  if (!entry || entry.snapshots.length === 0) {
    return { deltas: new Map(), snappedAt: null };
  }

  const prior = entry.snapshots[entry.snapshots.length - 1];
  const snappedAt = prior.snappedAt;

  const deltas = new Map();
  for (const row of currentRows) {
    const cc = row.countryCode;
    if (!cc) continue;
    const priorCountry = prior.countries[cc];
    const currentScore = row.popularityScore ?? null;
    if (priorCountry === undefined || priorCountry.popularityScore === null || currentScore === null) {
      deltas.set(cc, null);
    } else {
      const diff = currentScore - priorCountry.popularityScore;
      deltas.set(cc, { value: diff, snappedAt });
    }
  }

  return { deltas, snappedAt };
}

/**
 * Return a short ISO date string (YYYY-MM-DD) from an ISO 8601 timestamp.
 * Used for display: "vs your check on 2026-06-12".
 * @param {string} isoTimestamp
 * @returns {string}
 */
export function isoDateLabel(isoTimestamp) {
  if (!isoTimestamp) return '';
  const match = isoTimestamp.match(/^(\d{4}-\d{2}-\d{2})/);
  return match ? match[1] : isoTimestamp;
}
